/**
 * Tolt affiliate / referral tracking.
 *
 * The Tolt script (public/index.html -> https://files.tlt-cdn.com/tlt.js) captures
 * the referral from the marketing site's ?via= / ?ref= param and stores it in a
 * cookie scoped to the registrable domain (trulyinbox.com). Because www.trulyinbox.com
 * and app.trulyinbox.com share that root domain, the cookie is readable here and is
 * exposed as window.tolt_referral.
 *
 * The script only tracks the click. Converting a referred visitor into a Tolt "lead"
 * requires calling window.tolt.signup(email) once, when a NEW account is created.
 * Call trackToltSignup at the same points where pushGtmSignupComplete fires.
 */

interface ToltApi {
  signup?: (email: string) => Promise<unknown>;
}

declare global {
  interface Window {
    tolt?: ToltApi;
    // window.tolt_referral is declared in src/index.tsx
  }
}

/**
 * Registers the signed-up user as a Tolt lead when they arrived through an
 * affiliate link. No-op when there is no referral, no email, or the Tolt script
 * has not finished loading. Best-effort and non-blocking: tracking failures must
 * never break the signup flow.
 */
export function trackToltSignup(email?: string | null): void {
  if (typeof window === 'undefined') {
    return;
  }

  const referral = window.tolt_referral;
  const signup = window.tolt?.signup;

  // Attribute only referred signups; skip organic ones.
  if (!email || !referral || typeof signup !== 'function') {
    return;
  }

  try {
    Promise.resolve(signup(email)).catch(() => {
      /* no-op: Tolt lead tracking is best-effort */
    });
  } catch {
    /* no-op */
  }
}
